(() => {
  'use strict';
  if (window.OleaMotion?.version === 3) return;
  const body = document.body;
  if (!body) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const selector = '[data-ol-steps],.ol-stars,[data-ol-count],[data-ol-bar],[data-ol-img]';
  const candidates = `${selector},.ol-pc,[data-ol-read]`;
  const states = new Map();
  const queued = new Set();
  let enabled = false;
  let initialized = false;
  let observer;
  let contentFrame = 0;
  let scrollFrame = 0;
  let readBar;
  let initialScan = true;
  const within = (root, query) => {
    const elements = [];
    if (root?.nodeType === 1 && root.matches(query)) elements.push(root);
    if (root?.querySelectorAll) elements.push(...root.querySelectorAll(query));
    return elements;
  };
  function numberFormat(original) {
    const match = original.match(/^([^\d]*)(\d(?:[\d\s\u00a0\u202f.,]*\d)?)([^\d]*)$/u);
    if (!match || /[€£$¥]/u.test(original)) return null;
    const token = match[2];
    const language = document.documentElement.lang.toLowerCase();
    const preferred = /^(fr|de|es|it|pt|nl)(-|$)/u.test(language) ? ',' : '.';
    let decimal = '';
    const lastComma = token.lastIndexOf(',');
    const lastPoint = token.lastIndexOf('.');
    if (lastComma >= 0 && lastPoint >= 0) decimal = lastComma > lastPoint ? ',' : '.';
    else if (token.includes(preferred)) decimal = preferred;
    else if (/[.,]/u.test(token) && !/^\d{1,3}(?:[.,]\d{3})+$/u.test(token)) decimal = token.includes(',') ? ',' : '.';
    const split = decimal ? token.lastIndexOf(decimal) : -1;
    const integer = split >= 0 ? token.slice(0, split) : token;
    const fraction = split >= 0 ? token.slice(split + 1) : '';
    if (fraction && !/^\d+$/u.test(fraction)) return null;
    const groups = integer.match(/[\s\u00a0\u202f.,]/u);
    const separator = groups?.[0] || '';
    if (separator && !/^\d{1,3}(?:[\s\u00a0\u202f.,]\d{3})+$/u.test(integer)) return null;
    const target = Number(`${integer.replace(/[\s\u00a0\u202f.,]/gu, '')}${fraction ? `.${fraction}` : ''}`);
    if (!Number.isFinite(target) || target <= 0 || fraction.length > 3) return null;
    return { target, format(value) {
      const parts = value.toFixed(fraction.length).split('.');
      const whole = separator ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/gu, separator) : parts[0];
      return `${match[1]}${whole}${fraction ? `${decimal}${parts[1]}` : ''}${match[3]}`;
    } };
  }
  function stopCount(el, state) {
    if (state.kind !== 'count') return;
    if (state.frame) cancelAnimationFrame(state.frame);
    state.frame = 0;
    if (state.original !== undefined && el.textContent !== state.original) el.textContent = state.original;
    if (state.minWidth !== undefined) {
      el.style.minWidth = state.minWidth;
      state.minWidth = undefined;
    }
    el.removeAttribute('data-ol-counting');
  }
  function count(el, state) {
    if (!state.number || Number.parseFloat(getComputedStyle(el).fontSize) < 24) return;
    const width = el.getBoundingClientRect().width;
    state.minWidth = el.style.minWidth;
    if (width > 0) el.style.minWidth = `${width}px`;
    const started = performance.now();
    el.setAttribute('data-ol-counting', '');
    const step = now => {
      state.frame = 0;
      if (!enabled || !el.isConnected) { stopCount(el, state); return; }
      const progress = Math.min(1, Math.max(0, (now - started) / 1500));
      el.textContent = progress < 1 ? state.number.format(state.number.target * (1 - (1 - progress) ** 3)) : state.original;
      if (progress < 1) state.frame = requestAnimationFrame(step);
      else stopCount(el, state);
    };
    step(started);
  }
  function finish(el, state) {
    if (state.revealFrame) cancelAnimationFrame(state.revealFrame);
    state.revealFrame = 0;
    observer?.unobserve(el);
    state.finished = true;
    el.classList.remove('is-armed');
    el.classList.add('is-in');
    if (state.kind === 'count') stopCount(el, state);
  }
  function enter(el) {
    const state = states.get(el);
    if (!state || state.finished) return;
    observer?.unobserve(el);
    state.revealFrame = requestAnimationFrame(() => {
      state.revealFrame = 0;
      if (!enabled || !el.isConnected) { finish(el, state); return; }
      if (el.hasAttribute('data-ol-img') && el.getBoundingClientRect().height < 220) { finish(el, state); return; }
      state.finished = true;
      el.classList.add('is-in');
      if (state.kind === 'count') count(el, state);
    });
  }
  function arm(el) {
    let state = states.get(el);
    const kind = el.hasAttribute('data-ol-count') ? 'count' : 'reveal';
    const original = kind === 'count' ? (el.getAttribute('data-ol-count') || el.textContent.trim()) : undefined;
    if (state && (kind !== 'count' || state.original === original)) return;
    if (state) stopCount(el, state);
    state = { kind, original, finished: false };
    states.set(el, state);
    if (kind === 'count') {
      el.setAttribute('data-ol-count', original);
      state.number = numberFormat(original);
      if (!state.number || el.childElementCount || el.closest('.price,[data-price]')) { state.kind = 'static'; finish(el, state); return; }
    }
    const rect = el.hasAttribute('data-ol-img') && initialScan ? el.getBoundingClientRect() : null;
    if (!enabled || (rect && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0)) { finish(el, state); return; }
    el.classList.remove('is-in');
    el.classList.add('is-armed');
    observer.observe(el);
  }
  function syncProgress(root) {
    within(root, '[data-ol-native-progress]').forEach(progress => {
      const fill = progress.nextElementSibling?.querySelector('[data-ol-bar]');
      if (!fill) return;
      const value = Number(progress.getAttribute('value'));
      const max = Number(progress.getAttribute('max')) || 100;
      fill.style.width = `${Math.min(100, Math.max(0, value / max * 100))}%`;
    });
  }
  function syncReadBar() {
    const required = body.classList.contains('template-article') || document.querySelector('[data-ol-read]');
    if (!required) { readBar?.remove(); readBar = null; return; }
    readBar = document.querySelector('.ol-read');
    if (!readBar) {
      readBar = document.createElement('div');
      readBar.className = 'ol-read';
      readBar.setAttribute('aria-hidden', 'true');
      body.append(readBar);
    }
  }
  function updateScroll() {
    scrollFrame = 0;
    if (readBar) {
      const doc = document.documentElement;
      const ratio = Math.min(1, Math.max(0, doc.scrollTop / Math.max(1, doc.scrollHeight - doc.clientHeight)));
      readBar.style.transform = `scaleX(${ratio})`;
    }
  }
  function queueScroll() {
    if (!scrollFrame && readBar) scrollFrame = requestAnimationFrame(updateScroll);
  }
  function init(root = document) {
    syncProgress(root);
    within(root, selector).forEach(arm);
    syncReadBar();
    queueScroll();
  }
  function prune() {
    states.forEach((state, el) => {
      if (el.isConnected) return;
      stopCount(el, state);
      if (state.revealFrame) cancelAnimationFrame(state.revealFrame);
      observer?.unobserve(el);
      states.delete(el);
    });
  }
  function flushContent() {
    contentFrame = 0;
    prune();
    const roots = [...queued];
    queued.clear();
    roots.filter(root => root === document || root.isConnected).forEach(init);
  }
  function queueContent(root) {
    if (!root?.querySelectorAll) return;
    queued.add(root);
    if (!contentFrame) contentFrame = requestAnimationFrame(flushContent);
  }
  function syncEnabled() {
    const next = body.classList.contains('animations-enabled') && !reduced.matches && typeof IntersectionObserver === 'function';
    if (initialized && enabled === next) return;
    initialized = true;
    enabled = next;
    body.classList.toggle('olea-motion-ready', enabled);
    observer?.disconnect();
    if (enabled) {
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (entry.isIntersecting) enter(entry.target); });
      }, { rootMargin: '0px 0px -8% 0px' });
    }
    states.forEach((state, el) => {
      if (!enabled) finish(el, state);
      else if (!state.finished) observer.observe(el);
    });
    init(document);
  }
  window.OleaMotion = { version: 3, init };
  syncEnabled();
  initialScan = false;
  const contentObserver = new MutationObserver(records => {
    let removed = false;
    records.forEach(record => {
      if (record.type === 'attributes') {
        if (record.target.matches('[data-ol-native-progress]')) syncProgress(record.target);
        return;
      }
      if ([...record.removedNodes].some(node => node.nodeType === 1 && (node.matches(candidates) || node.querySelector(candidates)))) removed = true;
      record.addedNodes.forEach(node => {
        if (node.nodeType === 1 && (node.matches(candidates) || node.querySelector(candidates))) queueContent(node);
      });
    });
    if (removed && !contentFrame) contentFrame = requestAnimationFrame(flushContent);
  });
  contentObserver.observe(body, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'max'] });
  const settingsObserver = new MutationObserver(syncEnabled);
  settingsObserver.observe(body, { attributes: true, attributeFilter: ['class'] });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', () => {
    if (!contentFrame) contentFrame = requestAnimationFrame(flushContent);
  });
  document.addEventListener('olea:content-updated', event => queueContent(event.detail?.container || event.target || document));
  document.addEventListener('olea:variant-change', event => queueContent(event.target || document));
  window.addEventListener('scroll', queueScroll, { passive: true });
  window.addEventListener('resize', queueScroll, { passive: true });
  reduced.addEventListener('change', syncEnabled);
})();
